/**
 * Dashboard KPI Cards
 * @see Sprint 28 - US-28.2
 */

import { Package, PackageX, TrendingDown, ClipboardCheck } from 'lucide-react';
import { KPICard, Skeleton } from '@/components/ui';
import { useCanSeeFinancials, useCurrency } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useStockKPIs, useLossKPIs, useLastInventory } from '../hooks';

export type DashboardPeriod = '7days' | '30days' | 'month';

interface DashboardKPIsProps {
  period: DashboardPeriod;
}

const periodLabels: Record<DashboardPeriod, string> = {
  '7days': '7j',
  '30days': '30j',
  'month': 'mois',
};

export function DashboardKPIs({ period }: DashboardKPIsProps) {
  const canSeeFinancials = useCanSeeFinancials();
  const currency = useCurrency();
  const { data: stockKPIs, isLoading: stockLoading } = useStockKPIs();
  const { data: lossKPIs, isLoading: lossLoading } = useLossKPIs();
  const { data: lastInventory, isLoading: inventoryLoading } = useLastInventory();

  const isLoading = stockLoading || lossLoading || inventoryLoading;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
    );
  }

  // KPI 1: Stock value or product count
  const stockValue = stockKPIs?.totalStockValue ?? 0;
  const totalProducts = stockKPIs?.totalProducts ?? 0;

  // KPI 2: Low stock + critical
  const lowStockTotal = (stockKPIs?.lowStockCount ?? 0) + (stockKPIs?.criticalStockCount ?? 0);

  // KPI 3: Losses for period
  const lossData = period === '7days' ? lossKPIs?.last7Days : lossKPIs?.currentMonth;
  const lossValue = lossData?.totalValue ?? 0;
  const lossCount = lossData?.count ?? 0;

  // KPI 4: Last inventory variance
  let variancePercent: string | undefined;
  let varianceSubtitle: string | undefined;
  let varianceBadge: { label: string; variant: 'success' | 'warning' } | undefined;

  if (lastInventory && stockValue > 0) {
    const pct = (lastInventory.totalVarianceValue / stockValue) * 100;
    variancePercent = `${pct >= 0 ? '' : ''}${pct.toFixed(1)}%`;
    varianceSubtitle = canSeeFinancials
      ? `Ecart ${formatCentimes(lastInventory.totalVarianceValue, currency)}`
      : undefined;
    varianceBadge = Math.abs(pct) <= 3
      ? { label: 'OK', variant: 'success' }
      : { label: 'Attention', variant: 'warning' };
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* KPI 1: Stock Value or Product Count */}
      <KPICard
        title={canSeeFinancials ? 'Valeur de stock' : 'Produits en stock'}
        value={canSeeFinancials ? formatCentimes(stockValue, currency) : totalProducts}
        subtitle={canSeeFinancials ? 'Stock total valorise' : 'Produits en stock'}
        icon={<Package className="h-5 w-5" />}
      />

      {/* KPI 2: Low Stock */}
      <KPICard
        title="Ruptures a risque"
        value={lowStockTotal}
        subtitle="Produits sous stock min."
        icon={<PackageX className="h-5 w-5" />}
        badge={
          lowStockTotal > 0
            ? { label: 'Urgent', variant: 'danger' }
            : { label: 'OK', variant: 'success' }
        }
      />

      {/* KPI 3: Losses */}
      <KPICard
        title={`Pertes (${periodLabels[period]})`}
        value={canSeeFinancials ? formatCentimes(lossValue, currency) : lossCount}
        subtitle={canSeeFinancials ? `${lossCount} pertes declarees` : 'Pertes declarees'}
        icon={<TrendingDown className="h-5 w-5" />}
      />

      {/* KPI 4: Last Inventory Variance */}
      <KPICard
        title="Dernier ecart inventaire"
        value={variancePercent ?? '--'}
        subtitle={varianceSubtitle ?? (lastInventory ? undefined : 'Aucun inventaire valide')}
        icon={<ClipboardCheck className="h-5 w-5" />}
        badge={varianceBadge}
      />
    </div>
  );
}
